import Config from '@/config'
import { ApiRequestError } from '@/errors/ApiRequestError'
import { logger } from '@/lib/logger'
import type { ApisauceInstance } from 'apisauce'
import { create } from 'apisauce'
import { authRequestInterceptor } from './interceptors/auth'
import type { LeaveFeedbackRequest } from './types/Feedback'
import type {
	CompletedForm,
	CompletedFormDetail,
	FormEntry,
	PageResponse,
	PendingForm,
	SubmitPendingFormRequest,
} from './types/Forms'
import type {
	CardSetup,
	PatientBillingPrimaryCard,
	PatientBillingRecord,
	PatientInfo,
	PatientMembershipCheckResponse,
	PaymentPlan,
	WhiteningOrder,
	WhiteningOrderKind,
	WhiteningSummary,
} from './types/PatientApi'

/**
 * An error body read back through `responseType: 'arraybuffer'` arrives as bytes rather than
 * parsed JSON, since the response type is fixed for the whole request. Decoded only on the
 * failure path, so the PDF itself is never routed through this.
 */
const decodeArrayBufferError = (data: unknown): unknown => {
	if (!(data instanceof ArrayBuffer)) return data
	try {
		const text = String.fromCharCode(...new Uint8Array(data))
		return JSON.parse(text)
	} catch {
		return undefined
	}
}

/** The server's own wording when it refuses, e.g. "Looks like you already submitted the form", and its code. */
const resolveErrorBody = (data: unknown, fallback: string) => {
	const body =
		data && typeof data === 'object'
			? (data as { message?: unknown; code?: unknown })
			: {}
	const message =
		typeof body.message === 'string' && body.message.trim() ? body.message : fallback
	const code = typeof body.code === 'string' ? body.code : undefined
	return { message, code }
}

export class PatientApi {
	_apiConfig: ApisauceInstance

	constructor() {
		this._apiConfig = create({
			baseURL: Config.PATIENT_API_URL_BASE,
			timeout: 10000,
		})

		this._apiConfig.axiosInstance.interceptors.request.use(authRequestInterceptor)
	}

	getPatientInfo() {
		return this._apiConfig
			.get('/patients/me')
			.then((response) => response.data as PatientInfo)
			.catch((error) => {
				logger.error(new Error('failed_to_get_patient_info', { cause: error }))
				return {} as PatientInfo
			})
	}

	getBillingRecord() {
		return this._apiConfig
			.get('/billing/patient-record')
			.then((response) => response.data as PatientBillingRecord)
			.catch((error) => {
				logger.error(new Error('failed_to_get_patient_billing_info', { cause: error }))
				return {} as PatientBillingRecord
			})
	}

	/** The caller's payment plans with their instalments. Throws on failure, unlike the record. */
	getPaymentPlans() {
		return this._apiConfig
			.get('/payments/plans/mine', { page: 0, size: 50, sort: 'id,desc' })
			.then((response) => {
				if (!response.ok) {
					throw new Error(`failed_to_get_payment_plans: ${response.problem}`)
				}
				const data = response.data as PaymentPlan[] | PageResponse<PaymentPlan>
				return Array.isArray(data) ? data : data.content
			})
	}

	/** What Stripe's card UI needs to collect a card for this member. */
	startCardSetup({ apiVersion }: { apiVersion?: string }) {
		return this._apiConfig
			.post('/billing/cards/patient/setup', { apiVersion })
			.then((response) => {
				if (!response.ok)
					throw new Error(`failed_to_start_card_setup: ${response.problem}`)
				return response.data as CardSetup
			})
	}

	/** The card a confirmed setup attached becomes the member's default. */
	completeCardSetup({ setupIntentId }: { setupIntentId: string }) {
		return this._apiConfig
			.post('/billing/cards/patient/setup/complete', { setupIntentId })
			.then((response) => {
				if (!response.ok)
					throw new Error(`failed_to_complete_card_setup: ${response.problem}`)
				return response.data as PatientBillingPrimaryCard
			})
	}

	updatePrimaryCard({ token }: { token: string }) {
		return this._apiConfig
			.post('/billing/cards/patient/default', { token })
			.then((response) => response.data as PatientBillingPrimaryCard)
			.catch((error) => {
				logger.error(new Error('failed_to_update_primary_card', { cause: error }))
				return null
			})
	}

	updatePatientProfile({
		firstName,
		lastName,
		phone,
	}: { firstName?: string; lastName?: string; phone?: string }) {
		return this._apiConfig
			.post('/patients/update-info', { firstName, lastName, phone })
			.catch((error) => {
				logger.error(new Error('failed_to_update_patient_info', { cause: error }))
			})
	}

	getMembershipStatus() {
		return this._apiConfig.get('/patients/membership').then((response) => {
			return response.data as PatientMembershipCheckResponse
		})
	}

	/**
	 * The whitening kit in one read: the live order, and what may be ordered next.
	 *
	 * Throws rather than answering with an empty shape, because there is no honest empty one
	 * here — a summary that could not be read is not a member without a kit, and the screen has
	 * to be able to say so instead of offering a claim that would only 409.
	 */
	getWhiteningSummary() {
		return this._apiConfig.get('/whitening/summary').then((response) => {
			if (!response.ok) {
				throw new Error(`failed_to_get_whitening_summary: ${response.problem}`)
			}
			return response.data as WhiteningSummary
		})
	}

	/**
	 * Claims the kit, or asks for a gel refill.
	 *
	 * Carries the server's own code off a 409 — KIT_ALREADY_ORDERED, GEL_BEFORE_KIT,
	 * GEL_TOO_SOON, SHIPPING_ADDRESS_MISSING — so the screen can say which of those happened
	 * rather than "something went wrong". A missing address in particular is the one the member
	 * can go and fix.
	 */
	createWhiteningOrder({ kind }: { kind: WhiteningOrderKind }) {
		return this._apiConfig.post('/whitening/orders', { kind }).then((response) => {
			if (!response.ok) {
				const { message, code } = resolveErrorBody(
					response.data,
					`failed_to_create_whitening_order: ${response.problem}`
				)
				throw new ApiRequestError(message, response.status, code)
			}
			return response.data as WhiteningOrder
		})
	}

	/** Forms waiting on the signed-in patient. A failed read hides them rather than blocking the home screen. */
	getPendingForms() {
		return this._apiConfig
			.get('/forms/pending')
			.then((response) => {
				if (!response.ok) {
					throw new Error(`failed_to_get_pending_forms: ${response.problem}`)
				}
				return response.data as PendingForm[]
			})
			.catch((error) => {
				logger.error(new Error('failed_to_get_pending_forms', { cause: error }))
				return [] as PendingForm[]
			})
	}

	/** Newest first: the order is the caller's to choose, as with every list here. */
	getCompletedForms(page: number, size: number) {
		return this._apiConfig
			.get('/forms/completed', { page, size, sort: 'id,desc' })
			.then((response) => {
				if (!response.ok) {
					throw new Error(`failed_to_get_completed_forms: ${response.problem}`)
				}
				return response.data as PageResponse<CompletedForm>
			})
	}

	getCompletedForm(id: number) {
		return this._apiConfig.get(`/forms/completed/${id}`).then((response) => {
			if (!response.ok) {
				throw new Error(`failed_to_get_completed_form: ${response.problem}`)
			}
			return response.data as CompletedFormDetail
		})
	}

	/**
	 * Unlike the readers above this one throws, so the runner can tell the patient the answers
	 * did not go through instead of thanking them for a submission that never happened.
	 */
	submitPendingForm(id: number, entries: FormEntry[], definitionVersion: number) {
		const body: SubmitPendingFormRequest = { entries, definitionVersion }

		return this._apiConfig.post(`/forms/pending/${id}/submit`, body).then((response) => {
			if (!response.ok) {
				const { message, code } = resolveErrorBody(
					response.data,
					`failed_to_submit_form: ${response.problem}`
				)
				throw new ApiRequestError(message, response.status, code)
			}
		})
	}

	/**
	 * How likely the caller is to recommend Wally, and why.
	 *
	 * Throws like the form submit does rather than answering quietly, so the flow can say the
	 * answer did not go through instead of thanking somebody for one that never arrived. The
	 * patient is taken from the token at the other end and is never sent from here.
	 */
	leaveFeedback(score: number, comment?: string) {
		const body: LeaveFeedbackRequest = { score, comment }

		return this._apiConfig.post('/feedback', body).then((response) => {
			if (!response.ok) {
				const { message, code } = resolveErrorBody(
					response.data,
					`failed_to_leave_feedback: ${response.problem}`
				)
				throw new ApiRequestError(message, response.status, code)
			}
		})
	}

	/**
	 * The PDF receipt for one of the caller's own charges. 404 if the charge isn't theirs, 409
	 * with code CHARGE_NOT_PAID for a charge that never took money.
	 */
	getPaymentReceipt(paymentId: number) {
		return this._apiConfig
			.get<ArrayBuffer>(
				`/billing/payments/${paymentId}/receipt`,
				{},
				// The route only produces a PDF, and the server matches routes on Accept; the
				// client's default of application/json would find no route at all.
				{ responseType: 'arraybuffer', headers: { Accept: 'application/pdf' } }
			)
			.then((response) => {
				if (!response.ok) {
					const { message, code } = resolveErrorBody(
						decodeArrayBufferError(response.data),
						`failed_to_get_charge_receipt: ${response.problem}`
					)
					throw new ApiRequestError(message, response.status, code)
				}
				return response.data as ArrayBuffer
			})
	}
}

export const patientApi = new PatientApi()
